'use client';

import {
  Carousel,
  CarouselArrow,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from '@wwe-portal/ui/components/carousel';

import { FC, ReactNode, useMemo, useState } from 'react';
import {
  RosterCardData,
  RosterCardV2,
  RosterCardV2Skeleton,
} from './roster-card/roster-card-v2';
import { cn } from '@wwe-portal/ui/lib/lib';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { genArrayWithId } from '@wwe-portal/utils/array';
import {
  FlipCard,
  FlipCardBack,
  FlipCardFront,
  FlipCardTurnButton,
} from '@wwe-portal/ui/components/flip-card';
import { RosterCardBack } from './roster-card/roster-card-back';
import { SuperstarLink } from '@wwe-portal/web/components/common/link/superstar-link';
import { RosterWinrate } from './roster-card/roster-winrate';

export interface TrendingCardData extends RosterCardData {
  videoLinks?: {
    image: string;
    link: string;
  }[];
}

interface RosterTrendingCarouselProps {
  data: TrendingCardData[] | undefined;
  onItemClick?: (playerRoster: RosterCardData) => void;
  onItemFlip?: (playerRoster: RosterCardData) => void;
  imageRenderer?: (props: { superstar: RosterCardData }) => React.ReactNode;
  LinkComponent?: React.ComponentType<{
    superstarId: string;
    children: React.ReactNode;
  }>;
}

const RearGradient: FC<{ direction: 'left' | 'right' }> = ({ direction }) => {
  return (
    <div
      className={cn('pointer-events-none absolute inset-y-0 h-full', {
        'left-0': direction === 'left',
        'right-0 rotate-180': direction === 'right',
      })}
      style={{
        background:
          'linear-gradient(to right, #000000 0%, rgba(0, 0, 0, 0) 100%)',
        width: 'calc(50% - 128px)',
      }}
    ></div>
  );
};

const Layout: FC<{
  children: React.ReactNode;
  active?: boolean;
  contentClassName?: string;
}> = ({ children, active, contentClassName }) => {
  return (
    <Carousel
      opts={{
        loop: active,
        active,
        align: 'center',
        containScroll: 'trimSnaps',
        breakpoints: {
          '(min-width: 768px)': { dragFree: true },
        },
      }}
    >
      <CarouselContent
        className={cn('-ml-4', contentClassName)}
        containerClassName="justify-center flex"
      >
        {children}
      </CarouselContent>
      <RearGradient direction="left" />
      <RearGradient direction="right" />
      {active && <CarouselArrow />}
    </Carousel>
  );
};

const ItemLayout: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <CarouselItem className="drop-shadow-card h-[367px] w-full max-w-[232px]">
      {children}
    </CarouselItem>
  );
};

const CardFooterLayout: FC<{ title: ReactNode; talent: ReactNode }> = ({
  title,
  talent,
}) => {
  return (
    <div className="-mb-4 mt-6 flex h-[76px] w-full flex-col items-center gap-1">
      <div className="line-clamp-2 flex h-7 w-full items-center justify-center text-center text-[13px] font-medium uppercase leading-none text-white">
        {title}
      </div>
      <div className="h-8">{talent}</div>
    </div>
  );
};

const useCardFlipController = (data: TrendingCardData[] | undefined) => {
  const { api } = useCarousel();
  const [flippedId, setFlippedId] = useState<string | null>(null);
  const handleFlipBack = (id: string | undefined) => {
    if (!id) return;
    const index = data?.findIndex((item) => item.id === id);
    setFlippedId(id);
    if (index != null && index >= 0 && api) {
      api.scrollTo(index);
    }
  };

  const handleFlipFront = () => {
    setFlippedId(null);
  };

  return {
    flippedId,
    handleFlipBack,
    handleFlipFront,
  };
};

const Content: FC<RosterTrendingCarouselProps> = ({
  data,
  onItemClick,
  onItemFlip,
  imageRenderer,
  LinkComponent,
}) => {
  const { isHydrated } = useResponsive();
  const { flippedId, handleFlipBack, handleFlipFront } =
    useCardFlipController(data);
  return (
    <>
      {data?.map((item) => {
        const cardElement =
          isHydrated && item.id ? (
            <RosterCardV2
              data={item}
              onClick={() => onItemClick?.(item)}
              imageElement={
                <SuperstarLink superstarId={item.id}>
                  {imageRenderer?.({ superstar: item })}
                </SuperstarLink>
              }
              footerElement={
                <CardFooterLayout
                  title={<em>{item.fullName}</em>}
                  talent={
                    item.winPercentage != null ? (
                      <>
                        <div className="mb-2 mt-1 border-t border-[#797979] opacity-70" />
                        <RosterWinrate winPercentage={item.winPercentage} />
                      </>
                    ) : null
                  }
                />
              }
            />
          ) : null;

        return (
          <ItemLayout key={item.id}>
            <FlipCard className="h-full w-full" flip={flippedId === item.id}>
              <FlipCardFront>
                {cardElement}
                <FlipCardTurnButton
                  className="absolute bottom-2 right-3 rounded-full border border-[#007DC4] bg-[#9F211B] p-2"
                  iconClassName="w-2.5 h-2.5"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onItemFlip?.(item);
                    handleFlipBack(item.id);
                  }}
                />
              </FlipCardFront>
              <FlipCardBack>
                <RosterCardBack
                  videoLinks={item.videoLinks}
                  name={item.title}
                  tier={item.tier}
                  talentScore={item.talent}
                  totalUpgrades={item.totalUpgrades}
                  superstarId={item.id}
                  LinkComponent={LinkComponent}
                />
                <FlipCardTurnButton
                  className="absolute bottom-2 right-3 rounded-full border border-[#007DC4] bg-[#0d0d0d] p-2"
                  iconClassName="w-2.5 h-2.5"
                  onClick={handleFlipFront}
                />
              </FlipCardBack>
            </FlipCard>
          </ItemLayout>
        );
      })}
    </>
  );
};

export const RosterTrendingCarousel: FC<RosterTrendingCarouselProps> = ({
  data,
  onItemClick,
  onItemFlip,
  imageRenderer,
  LinkComponent,
}) => {
  return (
    <Layout active>
      <Content
        data={data}
        onItemClick={onItemClick}
        imageRenderer={imageRenderer}
        LinkComponent={LinkComponent}
        onItemFlip={onItemFlip}
      />
    </Layout>
  );
};

export const RosterTrendingCarouselSkeleton = () => {
  const data = useMemo(() => genArrayWithId(9), []);
  return (
    <Layout active={false} contentClassName="justify-center">
      {data.map((item) => (
        <ItemLayout key={item.id}>
          <RosterCardV2Skeleton
            hasFooter
            footerElement={<CardFooterLayout title={null} talent={null} />}
          />
        </ItemLayout>
      ))}
    </Layout>
  );
};
